export interface CaseStudy {
  slug: string
  brand: string
  logo: string
  logoBg: string
  tags: string[]
  title: string
  subtitle: string
  results: string[]
  about: {
    text: string
    website: string
  }
  challenge: string[]
  solution: {
    intro: string
    bullets: string[]
  }
  howItWorked: string[]
  resultsDetailed: string[]
  keyInsight?: string
  quote: {
    text: string
    author: string
    role: string
  }
}

export const CASE_STUDIES: CaseStudy[] = [
  {
    slug: 'clicks-talent',
    brand: 'Clicks Talent',
    logo: 'https://cdn.magicpatterns.com/uploads/oKbracun8P5MXQXuDPFXrA/clicks_talent_logo.webp',
    logoBg: 'bg-white',
    tags: ['Multi Agency Sourcing'],
    title: 'How Clicks Talent Powered a $300K Influencer Campaign Using Zano',
    subtitle:
      'Clicks Talent used Zano to source influencers across multiple agencies for a 3-month campaign, combining casting calls, roster discovery, and data-driven selection to move faster and close deals efficiently.',
    results: [
      '$300K campaign budget',
      '3-month ongoing campaign',
      'Sourced across multiple talent agencies',
      'Faster talent selection using built-in audience data',
    ],
    about: {
      text: 'Clicks Talent is a global influencer marketing and talent agency, working with brands like Warner Brothers, Skechers, and Uber Eats. As one of the first TikTok influencer agencies, they’ve been in the space for over 10 years, representing 700+ influencers while also sourcing and managing campaigns for brands — operating both as a talent agency and a marketing partner.',
      website: 'https://clickstalent.com/',
    },
    challenge: [
      'Before using Zano, sourcing talent across multiple agencies required reaching out to agencies one by one, waiting for responses and pricing, and manually comparing talent without standardized data.',
      'This slowed down campaign execution and made it harder to scale large influencer campaigns efficiently.',
    ],
    solution: {
      intro: 'Clicks Talent used Zano to centralize their sourcing process:',
      bullets: [
        'Casting calls to receive submissions from multiple talent agencies at once',
        'Roster database to proactively discover relevant influencers',
        'Built-in audience data to evaluate talent quickly and confidently',
        'Instead of fragmented communication, everything was accessible in one place.',
      ],
    },
    howItWorked: [
      'Posted a casting call with campaign requirements',
      'Received submissions from multiple talent agencies',
      'Used Zano’s roster database to discover additional influencers',
      'Reviewed talent using built-in audience and performance data',
      'Presented shortlisted creators to the client with full context',
      'Quickly aligned with agencies and locked in the deal',
    ],
    resultsDetailed: [
      'Successfully sourced talent for a total of $300K revenue of the campaign',
      'Reduced back-and-forth with agencies',
      'Enabled faster, data-driven decision making',
      'Built a scalable workflow for sourcing across agencies',
    ],
    quote: {
      text: 'Zano made it much easier to source and compare talent across agencies. Having everything quickly accessible so we can easily present it to our client helped us move faster and close the deal.',
      author: 'AB Lieberman',
      role: 'CEO & Founder of Clicks Talent',
    },
  },
  {
    slug: 'lemon-ads',
    brand: 'Lemon Ads',
    logo: 'https://cdn.magicpatterns.com/uploads/h4wb5JCZ5wgSQRDatB7XF2/lemond_adds_logo.webp',
    logoBg: 'bg-zinc-900',
    tags: ['Recurring Campaign'],
    title: 'How Lemon Ads Scaled to $40K in Campaign Revenue Through Zano',
    subtitle:
      'Lemon Ads used Zano to source influencers for their eCommerce client, turning a single connection into an ongoing campaign valued at $40K.',
    results: [
      '$40K+ in campaign revenue',
      'Started from a single influencer match',
      'Expanded into recurring collaboration',
      'Ongoing deals with Clicks Talent',
    ],
    about: {
      text: 'Lemon Ads is a performance marketing agency helping eCommerce and consumer brands scale through data-driven strategies, paid media, and influencer marketing. With a strong focus on ROI and measurable growth, they manage end-to-end campaigns — from sourcing creators to executing and optimizing high-performing influencer partnerships.',
      website: 'https://www.lemonads.com',
    },
    challenge: [
      'Finding the right influencers quickly — while maintaining quality — can be difficult when relying on fragmented outreach and limited visibility across talent agencies. For Lemon Ads, this meant spending time identifying relevant creators, reaching out individually, and managing multiple conversations across agencies, all while trying to move campaigns forward efficiently.',
      'With performance-driven campaigns, speed and quality are critical — and without a centralized way to discover and evaluate talent, scaling influencer campaigns becomes time-consuming and inconsistent.',
    ],
    solution: {
      intro:
        'Lemon Ads used Zano to streamline their sourcing process and gain access to a centralized network of represented influencers.',
      bullets: [
        'Posted a casting call for their campaign',
        'Received submissions from multiple talent agencies',
        'Discovered relevant creators in one place',
        'Connected directly with talent and agencies',
        'This allowed them to move faster while maintaining a high standard for talent selection.',
      ],
    },
    howItWorked: [
      'Clicks Talent submitted a relevant influencer',
      'Lemon Ads reviewed and selected the creator',
      'They connected and initiated the collaboration',
      'Clicks Talent expanded the opportunity by introducing additional influencers',
      'The collaboration evolved into a broader campaign across multiple creators',
    ],
    resultsDetailed: [
      'Initial match turned into a larger influencer collaboration',
      'Campaign scaled to $40K+ in revenue',
      'Built an ongoing working relationship with Clicks Talent',
      'Continued collaboration across new deals and campaigns',
      'Faster sourcing with higher-quality matches',
    ],
    keyInsight:
      'What started as a single influencer connection evolved into a long-term collaboration — highlighting how Zano enables not just discovery, but ongoing deal flow between agencies. Instead of one-off matches, agencies can build lasting partnerships that drive repeat campaigns and sustained revenue.',
    quote: {
      text: 'Zano helped us easily connect with new agencies and creators which saved us so much valuable time!!',
      author: 'Liora Mizrachi',
      role: 'Influencer Marketing Manager at Lemon Adds Marketing',
    },
  },
  {
    slug: 'people-first',
    brand: 'People First',
    logo: 'https://cdn.magicpatterns.com/uploads/2LDCJdigeDPe9MhEqnZJ4w/people_first_logo.webp',
    logoBg: 'bg-gradient-to-br from-blue-500 to-purple-500',
    tags: ['Long-Term Relationships'],
    title:
      'People First used Zano to Build an Ongoing Talent Agency Partnership',
    subtitle:
      'People First connected with a talent agency on Zano to support a nonprofit campaign, turning a single collaboration into multiple ongoing projects.',
    results: [
      '19,000+ views across campaign content',
      '3% engagement rate',
      'Successful nonprofit awareness campaign',
      'Ongoing collaboration across more projects',
    ],
    about: {
      text: 'People First is a mission-driven marketing agency focused on impact campaigns, working with organizations that promote social good, advocacy, and community-driven initiatives.',
      website: 'https://peoplefirst.cc/',
    },
    challenge: [
      'People First was working on a campaign for a nonprofit organization focused on supporting cancer patients and raising awareness. The challenge was finding the right influencer at the right time. An influencer with high quality content that aligns with the campaign’s mission — without relying on slow, manual outreach.',
    ],
    solution: {
      intro:
        'Using Zano, People First was able to connect directly with talent agencies representing influencers through the Roster Database.',
      bullets: [
        'Used filters on the roster database based on influencer requirements',
        'Reached out to the right talent manager as mentioned in Zano profile',
        'Collaborated directly on campaign needs and talent selection',
        'Zano enabled both sides to quickly align and move forward without fragmented communication.',
      ],
    },
    howItWorked: [
      'People First found influencer Philecia LaBounty from Clicks Talents roster in the marketplace',
      'People First shared details about their nonprofit campaign with the right talent manager',
      'After discussions, Philecia LaBounty was selected for the campaign',
      'Content was produced and published across TikTok and Instagram',
    ],
    resultsDetailed: [
      'Campaign content generated 19,000+ views',
      'Achieved a 3% engagement rate',
      'Helped the nonprofit expand awareness and reach a wider audience',
      'Delivered a meaningful, story-driven influencer campaign',
      'Clicks Talent and People First continued working together on multiple additional projects after the initial campaign.',
    ],
    keyInsight:
      'What started as a single connection evolved into an ongoing partnership — highlighting how Zano enables not just campaign execution, but long-term relationships between talent agencies and marketing agencies. This is where the real value of the marketplace emerges: repeat collaboration, not one-off deals.',
    quote: {
      text: 'We love using Zano and finding it very valuable to partner with talent agencies',
      author: 'Valerie Cheney',
      role: 'Vice President at People First Marketing',
    },
  },
  {
    slug: 'aimejay-media',
    brand: 'AimeJay Media',
    logo: 'https://cdn.magicpatterns.com/uploads/wAhi58zjBoWzfSKYDDf2s2/aimejaymedia_loho.webp',
    logoBg: 'bg-white',
    tags: ['Multi Agency Sourcing'],
    title: 'How AimeJay Media Sourced Represented Talent Through Zano in Days',
    subtitle:
      'Instead of relying on email blasts, LinkedIn posts, and personal networks, AimeJay Media used a single Zano casting call to connect with over 20 vetted talent agencies and find the right creator for a dating app campaign.',
    results: [
      'Reached more than 20 vetted talent agencies in days',
      'Received over 50 high quality creator submissions',
      'Evaluated talent using live performance and audience data',
      'Connected directly with the right representative',
      'Got a response from talent manager quickly',
    ],
    about: {
      text: "AimeJay Media is a boutique digital agency dedicated to helping brands tell their stories in ways that resonate. They collaborate with some of the industry's leading experts across SEO, paid advertising, UGC, content creation, and digital strategy, connecting clients with the right people and platforms to achieve their goals.",
      website: 'https://www.aimejaymedia.com/',
    },
    challenge: [
      'Marie Josée Elchab, Director of Influencer Marketing at AimeJay Media, was tasked with finding the right creator for a dating app campaign targeting Gen Z audiences.',
      "The client wanted an authentic creator who could naturally speak about dating, relationships, and meaningful connections. The ideal candidate needed a strong US-based audience, high engagement, and content that aligned with the campaign's message.",
      "Like many marketers, Marie faced a common challenge: finding represented creators isn't always easy. While there are plenty of platforms for discovering influencers, sourcing talent represented by agencies often depends on existing relationships, email outreach, LinkedIn posts, Slack communities, or waiting for referrals.",
      'Instead of spending weeks manually reaching out to agencies within her network, Marie decided to post the opportunity on Zano.',
    ],
    solution: {
      intro:
        "Using Zano's casting call marketplace, AimeJay Media published a brief outlining the campaign requirements and budget.",
      bullets: [
        'Within just a few days, the campaign generated 50+ creator submissions from 22 different vetted talent agencies.',
        'The response gave Marie access to a wide range of represented talent without having to manage dozens of separate outreach conversations.',
      ],
    },
    howItWorked: [
      'When Entertainment Lab submitted Jenna for consideration, they included pricing details and additional notes directly alongside the application.',
      'Zano provided live creator data, including follower count, average views, average likes, engagement rate, and audience demographics.',
      'Having pricing information, audience data, and performance metrics available in one place made it easy to evaluate Jenna as a strong candidate from the start.',
      "Zano provided direct contact information for Jenna's representative, eliminating the need to search for the right point of contact.",
      'Marie reached out to Sean from Entertainment Lab directly through the contact information provided on Zano, and conversations began immediately.',
    ],
    resultsDetailed: [
      'Reach more than 20 talent agencies',
      'Receive over 50 creator submissions in days',
      'Evaluate talent using live performance and audience data',
      'Connect directly with the right representative',
      'Secure the right creator for the campaign quickly and efficiently',
    ],
    keyInsight:
      'The influencer marketing industry has no shortage of creator discovery tools. However, represented talent remains fragmented across agency rosters, personal networks, inboxes, spreadsheets, and referral channels. Zano was built to solve that problem by creating a dedicated marketplace where brands, agencies, and talent representatives can connect efficiently.',
    quote: {
      text: 'Zano made the entire process seamless.',
      author: 'Marie Josée Elchab',
      role: 'Director of Influencer Marketing at AimeJay Media',
    },
  },
]

export const getCaseStudy = (slug: string) => {
  return CASE_STUDIES.find((cs) => cs.slug === slug)
}
